import { useState, useEffect } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/es";

dayjs.extend(customParseFormat);
dayjs.locale("es");

/**
 * Periodo de ingreso para NBAPI (0 = 1er cuatrimestre, 1 = 2do) a partir de YYYY-MM-DD,
 * sin pasar por Date (evita corrimientos de zona horaria).
 */
export const getPeriodoFromFecha = (fechaIngreso) => {
  if (!fechaIngreso) return 0;
  const month = parseInt(String(fechaIngreso).split('-')[1], 10);
  if (Number.isNaN(month)) return 0;
  return month >= 1 && month <= 7 ? 0 : 1;
};

/**
 * Fechas de ingreso que ofrece el checkout. Funcion pura: la usan el hook (para el combo del
 * form) y CheckoutPage (para pedir las carreras en la fase de carga), asi los dos parten de la
 * MISMA lista y de la MISMA fecha por defecto.
 *
 * - Cursos/eventos y microcredenciales (t = e | m) con ?fi=: la unica fecha es la del link.
 * - Resto: los dos proximos periodos (abril/agosto) segun la fecha de hoy, con cierre de
 *   inscripcion a agosto el 8 de septiembre.
 */
export const getAvailableDates = (fi, t, now = new Date()) => {
  if ((t === "e" || t === "m") && fi) {
    const string = dayjs(fi, "YYYY-MM-DD").format("MMMM YYYY");
    const label = string.charAt(0).toUpperCase() + string.slice(1);
    return [{ value: fi, label }];
  }

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const cierreAgosto = new Date(currentYear, 8, 8);
  const nextYear = currentYear + 1;

  if (currentMonth < 5) {
    return [
      { value: `${currentYear}-04-01`, label: `Abril ${currentYear}` },
      { value: `${currentYear}-08-01`, label: `Agosto ${currentYear}` },
    ];
  }
  if (now < cierreAgosto) {
    return [
      { value: `${currentYear}-08-01`, label: `Agosto ${currentYear}` },
      { value: `${nextYear}-04-01`, label: `Abril ${nextYear}` },
    ];
  }
  return [
    { value: `${nextYear}-04-01`, label: `Abril ${nextYear}` },
    { value: `${nextYear}-08-01`, label: `Agosto ${nextYear}` },
  ];
};

/**
 * Fecha de ingreso con la que arranca el checkout: la guardada (volver atras / recargar) si
 * sigue estando entre las disponibles; si no, ?fi= cuando coincide con una disponible; si no,
 * la primera disponible. Es la fecha con la que CheckoutPage pide las carreras y la que el
 * form muestra seleccionada, para que nunca difieran.
 */
export const getDefaultFechaIngreso = (fi, t, fechaGuardada = null) => {
  const dates = getAvailableDates(fi, t);
  const esDisponible = (v) => v && dates.some((d) => d.value === v);
  if (esDisponible(fechaGuardada)) return fechaGuardada;
  if (esDisponible(fi)) return fi;
  return dates[0]?.value ?? null;
};

const useAvailableDates = (fi, t) => {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setDates(getAvailableDates(fi, t));
    setLoading(false);
  }, [fi, t]);

  return { dates, loading };
};

export default useAvailableDates;
